import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import th from "date-fns/locale/th";
import { fetchIncomingBookingOverview } from "./incomingBookingAvailabilityQuery";
import IncomingSleepTestSchedule from "./IncomingSleepTestSchedule";
import { filterRecordsByStaffCustomerAccess } from "../../../utils/staffCustomerAccess";
import { parseIncomingDate, formatIncomingDate } from "./incomingPatientDates";
import { getIncomingBookingPolicy, incomingBookingDay, isIncomingBookingDayAllowed } from "./incomingBookingAvailability";

export default function IncomingAvailabilityOverview({ nodes, refreshKey, customer = "CHG03", customerOptions = [], onCustomerChange, currentUser, adminProfile, incomingPatients, onEditIncoming, children }) {
  const policy = getIncomingBookingPolicy(customer);
  const today = incomingBookingDay(new Date(Date.now()));
  const [nodeChoice, setNodeChoice] = useState("");
  const node = nodes.find((item) => item.id === nodeChoice) || nodes[0];
  const nodeId = node && node.id;
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [selected, setSelected] = useState(() => today);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState(null);
  const scope = `${nodeId}|${customer}|${month}|${reload}|${today}`;
  const current = result && result.scope === scope && result.refreshKey === refreshKey ? result : null;

  useEffect(() => {
    if (!nodeId) return undefined;
    let active = true;
    const [year, number] = month.split("-").map(Number);
    const first = [today, format(new Date(year, number - 2, 1), "yyyy-MM-dd")].sort().pop();
    const last = format(new Date(year, number + 1, 1), "yyyy-MM-dd");
    fetchIncomingBookingOverview(nodeId, first, last, customer).then(({ days, bookings }) => {
      if (active) setResult({ scope, refreshKey, first, last, days, bookings });
    }).catch(() => {
      if (active) setResult({ scope, refreshKey, error: true });
    });
    return () => { active = false; };
  }, [nodeId, customer, month, reload, refreshKey, scope, today]);

  const availability = (day) => day < today || !current || current.error || day < current.first || day >= current.last ? null :
    current.days[day] || { available: 0, total: 0, occupied: 0 };
  const selectedDay = availability(selected);
  const visibleBookings = filterRecordsByStaffCustomerAccess({
    records: current && !current.error ? current.bookings : [],
    currentUser, adminData: adminProfile, nodes,
    getNodeValue: () => nodeId,
    getCustomerValue: (booking) => (booking.extendedProps || {}).customer,
  });
  const tone = (info) => !info ? "unknown" : !info.total ? "empty" : !info.available ? "full" : info.available === 1 ? "limited" : "open";
  if (!node || !policy) return null;
  return (
    <>
    <section className="incoming-overview" aria-label={`${policy.customer} booking availability`}>
      {customerOptions.length > 1 && onCustomerChange && <div className="incoming-overview-customer-switch" role="group" aria-label="Availability calendar">
        {customerOptions.map((value) => <button type="button" key={value}
          className={`btn ${value === policy.customer ? "btn-primary" : "btn-outline-primary"}`}
          aria-pressed={value === policy.customer} onClick={() => onCustomerChange(value)}>{value}</button>)}
      </div>}
      <div className="incoming-overview-heading">
        <div><h5>ห้องว่าง {policy.customer}</h5><span>Incoming สูงสุด {policy.maxDaily} คน/วัน{policy.weekdays && " · พฤหัสบดี ศุกร์ เสาร์"}</span></div>
        <button type="button" className="btn btn-outline-primary" aria-label={`Refresh ${policy.customer} availability`} disabled={!current} onClick={() => setReload((value) => value + 1)}>อัปเดต</button>
      </div>
      <div className="incoming-overview-node-row">
      {nodes.length > 1 ? <div className="incoming-overview-node"><label htmlFor="incoming-availability-node">สถานที่ตรวจ / Node</label><select id="incoming-availability-node" className="form-control" aria-label="Availability node" value={nodeId} onChange={(event) => setNodeChoice(event.target.value)}>
        {nodes.map((item) => <option key={item.id} value={item.id}>{item.nodename}</option>)}
      </select></div> : <div className="incoming-overview-node">สถานที่ตรวจ: <strong>{node.nodename}</strong></div>}
      <button type="button" className="btn btn-outline-primary" onClick={() => { setSelected(today); setMonth(today.slice(0, 7)); }}>วันนี้</button>
      </div>
      <div className="incoming-overview-summary" aria-live="polite">
        {!current ? <span role="status">กำลังตรวจสอบห้องว่าง…</span> : current.error ?
          <span className="text-danger">โหลดห้องว่างไม่สำเร็จ กรุณากดอัปเดต</span> :
          <><div><span className="incoming-overview-selected-label">วันที่เลือก</span><strong>{formatIncomingDate(selected)}</strong></div><span className={`incoming-overview-result incoming-overview-result-${tone(selectedDay)}`}>
            {selected >= today && !isIncomingBookingDayAllowed(selected, customer) ? "ไม่รับจอง" : selectedDay ? !selectedDay.total ? "ไม่มีรอบจอง" : selectedDay.available ? `ว่าง ${selectedDay.available} ที่` : "เต็มแล้ว" : "ยังไม่ทราบ"}
          </span></>}
      </div>
      <DatePicker
        inline
        locale={th}
        renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
          <div className="incoming-calendar-navigation">
            <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="Previous month">‹ ก่อนหน้า</button>
            <strong>{format(date, "MMMM yyyy", { locale: th })}</strong>
            <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="Next month">ถัดไป ›</button>
          </div>
        )}
        minDate={parseIncomingDate(today)}
        filterDate={(date) => isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)}
        selected={parseIncomingDate(selected)}
        onChange={(date) => { if (date && format(date, "yyyy-MM-dd") >= today && isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)) { setSelected(format(date, "yyyy-MM-dd")); setMonth(format(date, "yyyy-MM")); } }}
        onMonthChange={(date) => {
          const day = format(date, "yyyy-MM-dd") < today ? today : format(date, "yyyy-MM-dd");
          setMonth(day.slice(0, 7)); setSelected(day);
        }}
        calendarClassName="incoming-availability-calendar incoming-overview-calendar"
        dayClassName={(date) => `incoming-availability-cell incoming-availability-cell-${format(date, "yyyy-MM-dd") < today ? "past" : !isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer) ? "empty" : tone(availability(format(date, "yyyy-MM-dd")))}`}
        renderDayContents={(day, date) => {
          if (format(date, "yyyy-MM-dd") < today) return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} วันที่ผ่านมา`}><span className="incoming-availability-day-number">{day}</span></span>;
          if (!isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)) return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} ไม่รับจอง`}><span className="incoming-availability-day-number">{day}</span><small>ไม่รับจอง</small></span>;
          const info = availability(format(date, "yyyy-MM-dd"));
          const label = !info ? "…" : !info.total ? "—" : info.available ? `ว่าง ${info.available}` : "เต็ม";
          return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} ${!info ? "ยังไม่ทราบ" : !info.total ? "ไม่มีรอบจอง" : label}`}>
            <span className="incoming-availability-day-number">{day}</span><small>{label}</small>
          </span>;
        }}
      />
      <div className="incoming-availability-legend mt-2 mb-2" aria-label="Availability legend">
        <span className="incoming-legend-open">ว่าง 2–{policy.maxDaily}</span><span className="incoming-legend-limited">ว่าง 1</span>
        <span className="incoming-legend-full">เต็ม</span><span className="incoming-legend-empty">— ไม่มีรอบ</span>
      </div>
    </section>
    <div className="incoming-workspace-details">
      {children}
      <IncomingSleepTestSchedule key={`${nodeId}|${customer}|${selected}`} day={selected} customer={policy.customer} nodeId={nodeId} nodeName={node.nodename}
        bookings={visibleBookings} incomingPatients={incomingPatients} onEditIncoming={onEditIncoming} loading={!current} error={Boolean(current && current.error)} />
    </div>
    </>
  );
}
